import {
  useCallback,
  useEffect,
  useMemo,
  lazy,
  Suspense,
  type ReactNode,
  useState,
} from 'react';
import { CircleQuestionMark } from 'lucide-react';
import { useAuth } from '@/hooks';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { useHouseholdStore } from '@/stores/householdStore';
import { authService } from '@/services';
import { captureEvent, captureEventOnceForUser } from '@/lib/analytics';
import { Button, IconButton, Loader } from '@/components/ui';
import { OnboardingLayout } from './OnboardingLayout';
import { OnboardingFooterProvider } from './OnboardingFooterContext';
import { TroubleshootingForwarding } from './TroubleshootingForwarding';
import {
  isRetryableWithSameAccount,
  type ForwardingBlock,
} from './forwardingBlock';
import type { DigestStage } from './StepDigestOptIn';
import { ONBOARDING_COPY } from './onboardingCopy';

const StepFounderLetter = lazy(() =>
  import('./StepFounderLetter').then((m) => ({
    default: m.StepFounderLetter,
  })),
);
const StepProfileSetup = lazy(() =>
  import('./StepProfileSetup').then((m) => ({ default: m.StepProfileSetup })),
);
const StepDemo = lazy(() =>
  import('./StepDemo').then((m) => ({ default: m.StepDemo })),
);
const StepAddChildren = lazy(() =>
  import('./StepAddChildren').then((m) => ({ default: m.StepAddChildren })),
);
const StepAddCollaborators = lazy(() =>
  import('./StepAddCollaborators').then((m) => ({
    default: m.StepAddCollaborators,
  })),
);
const StepSetupForwarding = lazy(() =>
  import('./StepSetupForwarding').then((m) => ({
    default: m.StepSetupForwarding,
  })),
);
const StepAutoForwarding = lazy(() =>
  import('./StepAutoForwarding').then((m) => ({
    default: m.StepAutoForwarding,
  })),
);
const StepApproveSenders = lazy(() =>
  import('./StepApproveSenders').then((m) => ({
    default: m.StepApproveSenders,
  })),
);
const StepDigestOptIn = lazy(() =>
  import('./StepDigestOptIn').then((m) => ({ default: m.StepDigestOptIn })),
);
const StepSummary = lazy(() =>
  import('./StepSummary').then((m) => ({ default: m.StepSummary })),
);

interface StepConfig {
  content: ReactNode;
  footer?: ReactNode;
  showBack?: boolean;
  onBack?: () => void;
  /** When true, render a `?` help button that opens the troubleshooting overlay. */
  showHelp?: boolean;
}

export interface OnboardingFlowProps {
  /**
   * Where the Founder's letter's chevron goes: back to the create-vs-join
   * fork (YAR-438, behaviour rule #5 — "Back navigation may reach the fork
   * freely before onboarding completes"). The container owns that
   * transition (`setEntryChoice(null)`); without it the letter has no
   * chevron, as it had before the fork existed. Re-picking admin on the
   * fork is the idempotent 409 resume, so leaving the letter never costs
   * the household that was already created.
   */
  onBackToFork?: () => void;
}

export function OnboardingFlow({ onBackToFork }: OnboardingFlowProps = {}) {
  const { user } = useAuth();
  const activeHouseholdId = useHouseholdStore((s) => s.householdId);
  const currentStep = useOnboardingStore((state) => state.currentStep);
  const forwardingStep = useOnboardingStore((state) => state.forwardingStep);
  const progress = useOnboardingStore((state) => state.progress);
  const canGoBack = useOnboardingStore((state) => state.canGoBack);
  const next = useOnboardingStore((state) => state.next);
  const back = useOnboardingStore((state) => state.back);
  const setForwardingStep = useOnboardingStore(
    (state) => state.setForwardingStep,
  );
  const completeOnboarding = useOnboardingStore(
    (state) => state.completeOnboarding,
  );
  const initFromUser = useOnboardingStore((state) => state.initFromUser);

  const [stepFooter, setStepFooter] = useState<ReactNode>(null);
  const footerContextValue = useMemo(() => ({ setFooter: setStepFooter }), []);

  // When Gate 2 (StepAutoForwarding) determines the connected mailbox is
  // unsupported, it routes the user back to the 'choice' connect screen and
  // we surface this inline notice with no Skip / no Back (a hard stop).
  const [forwardingNotice, setForwardingNotice] =
    useState<ForwardingBlock | null>(null);

  // Troubleshooting overlay for the forwarding flow. Local to this
  // component because it's a short-lived "read a few tips and come
  // back" side trip — no reason to model it as its own step.
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [digestStage, setDigestStage] = useState<DigestStage>('details');
  // Close the overlay whenever the user progresses to a different step
  // so it doesn't leak into a later screen after the user backs out.
  useEffect(() => {
    setShowTroubleshooting(false);
  }, [currentStep, forwardingStep]);

  // Seed store with auto-detected timezone when user is available
  useEffect(() => {
    if (user?.id) {
      const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      initFromUser(user.id, detectedTz);
      captureEventOnceForUser('onboarding_started', user.id, {
        entry_route: window.location.pathname,
      });
    }
  }, [user?.id, initFromUser]);

  useEffect(() => {
    captureEvent('onboarding_step_viewed', {
      step_key:
        currentStep === 'forwarding'
          ? `${currentStep}.${forwardingStep}`
          : currentStep,
    });
  }, [currentStep, forwardingStep]);

  // Detect return from Gmail OAuth redirect and advance forwarding sub-step
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail_connected') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      setForwardingStep('auto-loading');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount — only on initial load after OAuth redirect

  const suspenseFallback = (
    <div className="flex items-center justify-center py-20">
      <Loader size="lg" />
    </div>
  );

  // ── Forwarding sub-flow handlers ──────────────────────────────────────────
  const handleConnectGoogle = useCallback(async () => {
    // Dead-end verdict: the signed-in account itself is unusable (it is not a
    // personal Gmail). Since YAR-325 requires the connected mailbox to be the
    // signed-in account, there is nothing to retry here — attaching a
    // different mailbox is exactly what we now reject. The only recovery is
    // to come back as the account that owns a usable mailbox, so sign out
    // rather than re-opening Google's consent screen on a dead end.
    //
    // Retryable verdicts must NOT land here. `account_mismatch` never did;
    // `missing_scopes` did, and that was the bug (YAR-372): a parent who left
    // one permission box unchecked was signed out by the only button on the
    // screen, so they could never grant the rest on the account they wanted.
    // `requestGmailForwardingAccess` sends `prompt=consent`, so Google
    // re-renders the checkboxes and the second attempt can succeed.
    if (
      forwardingNotice &&
      !isRetryableWithSameAccount(forwardingNotice.reason)
    ) {
      await authService.signOut();
      return;
    }
    const returnUrl = `${window.location.origin}/?gmail_connected=1`;
    // `login_hint` preselects the signed-in account. It is only a hint —
    // `gmail-forwarding-setup` enforces the match server-side.
    await authService.requestGmailForwardingAccess(returnUrl, user?.email);
    // Execution stops here — browser redirects to Google
  }, [forwardingNotice, user?.email]);

  const handleBlocked = useCallback(
    (block: ForwardingBlock) => {
      setForwardingNotice(block);
      setForwardingStep('choice');
    },
    [setForwardingStep],
  );

  const handleAutoSuccess = useCallback(() => {
    // Clear any prior notice before advancing. Only the retryable reasons can
    // reach success with a notice still set (`non_personal_domain` signs the
    // user out instead of retrying), and leaving it set would make
    // StepApproveSenders treat the now-connected account as blocked and skip
    // setupGmailForwarding entirely.
    setForwardingNotice(null);
    setForwardingStep('approve-senders');
  }, [setForwardingStep]);

  const handleApproveSendersComplete = useCallback(() => {
    next();
  }, [next]);

  const handleDigestComplete = useCallback(() => {
    setDigestStage('details');
    next();
  }, [next]);

  const handleDigestSkip = useCallback(() => {
    setDigestStage('details');
    next();
  }, [next]);

  const handleDigestBack = useCallback(() => {
    if (digestStage === 'details') {
      back();
      return;
    }
    setDigestStage('details');
  }, [back, digestStage]);

  // Carousel footer buttons: "Next" for cards 1–3, "Continue" on the last
  // card — copy comes from ONBOARDING_COPY.carousel so it stays in sync
  // with the titles.
  const demoNextButton = (
    <Button
      variant="primary"
      fullWidth
      onClick={next}
    >
      {ONBOARDING_COPY.carousel.next}
    </Button>
  );
  const demoContinueButton = (
    <Button
      variant="primary"
      fullWidth
      onClick={next}
    >
      {ONBOARDING_COPY.carousel.continue}
    </Button>
  );

  // ── Forwarding sub-step config ────────────────────────────────────────────
  const getForwardingConfig = (): StepConfig => {
    switch (forwardingStep) {
      case 'choice':
        return {
          content: (
            <StepSetupForwarding
              onConnectGoogle={handleConnectGoogle}
              signedInEmail={user?.email}
              notice={forwardingNotice}
            />
          ),
          showHelp: true,
          // Hard stop only on a dead-end verdict: no Back affordance, no Skip.
          // A retryable one is not a hard stop — the parent is one consent
          // round trip away — so leave navigation intact (YAR-372).
          ...(forwardingNotice &&
          !isRetryableWithSameAccount(forwardingNotice.reason)
            ? { showBack: false }
            : {}),
        };
      case 'auto-loading':
        return {
          content: (
            <StepAutoForwarding
              user={user!}
              onSuccess={handleAutoSuccess}
              onBlocked={handleBlocked}
              onFailure={() => setForwardingStep('choice')}
            />
          ),
          showBack: false,
        };
      case 'approve-senders':
        return {
          content: (
            <StepApproveSenders
              user={user!}
              onComplete={handleApproveSendersComplete}
              accountBlocked={Boolean(forwardingNotice)}
            />
          ),
          onBack: () => setForwardingStep('choice'),
        };
      default:
        return { content: null };
    }
  };

  const getStepConfig = (): StepConfig => {
    switch (currentStep) {
      case 'founder-letter':
        // Step 0: the store's back() has nowhere to go, so the chevron is
        // the container's exit to the fork — and only exists when there is
        // one.
        return {
          content: <StepFounderLetter onContinue={next} />,
          showBack: Boolean(onBackToFork),
          onBack: onBackToFork,
        };
      case 'profile':
        return {
          content: (
            <StepProfileSetup
              user={user!}
              onComplete={next}
              // The household the fork created: the admin's family role is
              // saved to that membership (YAR-485).
              householdId={activeHouseholdId}
            />
          ),
        };
      case 'demo1':
        return { content: <StepDemo demoIndex={1} />, footer: demoNextButton };
      case 'demo2':
        return { content: <StepDemo demoIndex={2} />, footer: demoNextButton };
      case 'demo3':
        return { content: <StepDemo demoIndex={3} />, footer: demoNextButton };
      case 'demo4':
        return {
          content: <StepDemo demoIndex={4} />,
          footer: demoContinueButton,
        };
      case 'children':
        return {
          content: (
            <StepAddChildren
              user={user!}
              onComplete={next}
            />
          ),
        };
      case 'collaborators':
        return {
          content: (
            <StepAddCollaborators
              user={user!}
              onComplete={next}
              onSkip={next}
            />
          ),
        };
      case 'forwarding':
        return getForwardingConfig();
      case 'digest':
        return {
          content: (
            <StepDigestOptIn
              stage={digestStage}
              onStageChange={setDigestStage}
              onComplete={handleDigestComplete}
              onSkip={handleDigestSkip}
            />
          ),
          onBack: handleDigestBack,
        };
      case 'summary':
        return {
          content: (
            <StepSummary
              user={user!}
              onComplete={completeOnboarding}
            />
          ),
        };
      default:
        return { content: null };
    }
  };

  const stepConfig = getStepConfig();
  // Computed before the bare-render early return below, while `currentStep`
  // is still the full union — avoids an `as string` cast to defeat the
  // narrowing that early return would otherwise apply to this comparison.
  // The Founder's letter sits outside the step count (progress 0), so it
  // renders in the layout with the nav but without the bar — the same
  // treatment as the fork and the wait-state before it.
  const showProgressBar = currentStep !== 'founder-letter';

  // Summary renders without OnboardingLayout — no nav, no progress bar.
  if (currentStep === 'summary') {
    return (
      <Suspense fallback={suspenseFallback}>{stepConfig.content}</Suspense>
    );
  }

  const showBack =
    stepConfig.showBack !== undefined
      ? stepConfig.showBack
      : canGoBack ||
        (currentStep === 'forwarding' && forwardingStep !== 'choice');

  const handleBack = stepConfig.onBack ?? back;

  const headerRight = stepConfig.showHelp ? (
    <IconButton
      aria-label="Open troubleshooting"
      variant="ghost"
      onClick={() => setShowTroubleshooting(true)}
    >
      {/* 24px box, so the drawn circle is the frame's 20×20 (QA on
          YAR-160, Oct 2: it measured 16.67 at w-5). */}
      <CircleQuestionMark className="w-6 h-6 text-icon-default" />
    </IconButton>
  ) : undefined;

  return (
    <OnboardingFooterProvider value={footerContextValue}>
      <OnboardingLayout
        onBack={handleBack}
        showBackButton={showBack}
        showProgressBar={showProgressBar}
        progress={currentStep === 'digest' ? 90 : progress}
        headerRight={headerRight}
        stickyFooter={stepConfig.footer ?? stepFooter}
      >
        {/* flex-1/min-h-0 keeps the flex chain from OnboardingLayout's
            <main> intact so height-bounded steps (the video carousel)
            can size to the viewport; scroll-height steps are unaffected
            because overflow still propagates to <main>. */}
        <div className="ph-no-capture flex flex-1 flex-col min-h-0">
          <Suspense fallback={suspenseFallback}>{stepConfig.content}</Suspense>
        </div>
      </OnboardingLayout>
      {showTroubleshooting && (
        <TroubleshootingForwarding
          onBack={() => setShowTroubleshooting(false)}
        />
      )}
    </OnboardingFooterProvider>
  );
}
